import { round2 } from './validators/calc-diff.helper';

/**
 * Minimal structural shape the shared extracted-sum logic reads off a schema
 * control. Each sheet component declares its own local control interface
 * (SchemaControl / FieldControl / ControlConfig / FormControlSchema); all of
 * them are structurally assignable to this, so no component needs a shared
 * control type.
 */
export interface SummableControl {
  qname: string;
  type: string;
  ui: {
    calculated?: boolean;
    readonly?: boolean;
    editableTotal?: boolean;
    /**
     * Cross-sheet inheritance link. When present, this control's value is
     * inherited from `qname` on the sheet identified by the role code `role`
     * (e.g. role 8 `LongtermBorrowings` ← role 9 `210100`). The extracted-sum
     * baseline for such a control must come from the LINK TARGET's snapshot, not
     * the host sheet's own (stale) snapshot — see ExtractedSumWarningService.
     */
    linkTo?: { role?: string; qname?: string } | null;
  };
  validation?: {
    extractedSumWarning?: boolean;
  };
}

/**
 * The single standardized predicate for "is this a sum-value numeric total?".
 * A field qualifies iff it is a monetary item AND it is a derived total
 * (calculated OR readonly). This is the one definition every in-scope sheet
 * must use (replacing the per-sheet `isCalculatedField`/`isMonetaryItem` drift).
 */
export function isSummableMonetaryTotal(control: SummableControl): boolean {
  return (
    control.type === 'MonetaryItemType' &&
    (control.ui?.calculated === true || control.ui?.readonly === true)
  );
}

/**
 * Override axis #2: a qualifying field may opt out of the warning via
 * `validation.extractedSumWarning: false`. Default is enabled.
 */
export function extractedSumWarningEnabled(control: SummableControl): boolean {
  return control.validation?.extractedSumWarning !== false;
}

/** One mismatching column for a control. */
export interface ColumnMismatch {
  columnKey: string;
  label: string;
  baseline: number;
  current: number;
}

/** One column's baseline-presence status, for the debug overlay. */
export interface BaselineColumnStatus {
  label: string;
  present: boolean;
}

/**
 * Read-only twin of `compareColumns` for the Ctrl+Alt+B debug overlay.
 * Reports, per visible column, whether an N8N baseline value exists — and
 * unlike `compareColumns` it does NOT skip absent columns, because absence
 * is exactly the signal the overlay surfaces. A column is "present" when its
 * baseline rounds to a non-null number (so empty string / null / undefined
 * are absent; "0" is present).
 */
export function describeBaselineColumns(args: {
  controlQname: string;
  columns: { key: string; label: string }[];
  getControlName: (qname: string, columnKey: string) => string;
  getBaselineValue: (controlName: string) => unknown;
}): BaselineColumnStatus[] {
  return args.columns.map((col) => {
    const controlName = args.getControlName(args.controlQname, col.key);
    const baseline = round2(args.getBaselineValue(controlName));
    return { label: col.label, present: baseline !== null };
  });
}

/**
 * Compare one control's columns (current vs N8N baseline) and return the
 * mismatching ones. Mirrors the editor's skip rules: a column is skipped when
 * the baseline rounds to null (empty baseline -> no mismatch); an empty current
 * is treated as 0. Matches within 2dp rounding are not reported.
 */
export function compareColumns(args: {
  controlQname: string;
  /**
   * Qname used to build the BASELINE key, when it differs from `controlQname`.
   * For a linkTo control the current value is read under the host qname
   * (`controlQname`) but the baseline is read under the link target's qname
   * (`baselineControlQname`) from the target sheet's snapshot. Defaults to
   * `controlQname` (the normal same-field case).
   */
  baselineControlQname?: string;
  columns: { key: string; label: string }[];
  getControlName: (qname: string, columnKey: string) => string;
  getCurrentValue: (controlName: string) => unknown;
  getBaselineValue: (controlName: string) => unknown;
}): ColumnMismatch[] {
  const out: ColumnMismatch[] = [];
  const baselineQname = args.baselineControlQname ?? args.controlQname;
  for (const col of args.columns) {
    const baselineName = args.getControlName(baselineQname, col.key);
    const baseline = round2(args.getBaselineValue(baselineName));
    if (baseline === null) continue; // no baseline -> no mismatch
    const currentName = args.getControlName(args.controlQname, col.key);
    const current = round2(args.getCurrentValue(currentName)) ?? 0;
    if (current !== baseline) {
      out.push({ columnKey: col.key, label: col.label, baseline, current });
    }
  }
  return out;
}
